// The SUPPORT card raised when a company's old automatic payments card couldn't be removed at Stripe after a change of
// card (#25 phase 6, G13), or (#40) once its automatic payments were stopped: what public-ledger-autopay-card-switch (or
// stop-finish) wrote on the job, for support to remove it by hand. Pure, so the unit tests import it.
import { formatDate } from '../../../../helpers/format.js';
import { cardText } from '../../../../helpers/auto-payments.js';

type Metadata = ReadonlyArray<{ key?: string | null; value?: string | null } | null | undefined> | null | undefined;

const text = (metadata: Metadata, key: string): string | null => {
  const value = (metadata ?? []).find((m) => m?.key === key)?.value;
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
};

// Only Stripe's own dashboard, for a customer id, may be linked: the link is built from the job, so nothing else is opened
const STRIPE_CUSTOMER_URL = /^https:\/\/dashboard\.stripe\.com\/(test\/)?customers\/cus_[A-Za-z0-9]+$/;

export interface CardDetachFailed {
  companyName: string;
  companyId: string | null;
  oldCard: string | null;
  oldPaymentMethodId: string | null;
  stripeCustomerId: string | null;
  // The Stripe dashboard page of the customer, when the job names one of Stripe's own
  stripeCustomerUrl: string | null;
  newCard: string | null;
  // #40: no new card replaced it: automatic payments were stopped
  stopped: boolean;
  error: string | null;
  // When removing it failed, as a long date and time, or null
  failedAt: string | null;
}

export function cardDetachFailed(metadata: Metadata): CardDetachFailed {
  const url = text(metadata, 'STRIPE_CUSTOMER_URL');
  const failedAt = text(metadata, 'FAILED_AT');
  const failed = failedAt ? formatDate(failedAt, 'dateTime') : '';
  return {
    companyName: text(metadata, 'subjectCompanyName') ?? 'The company',
    companyId: text(metadata, 'SUBJECT_COMPANY_ID'),
    oldCard: cardText({ cardBrand: text(metadata, 'OLD_CARD_BRAND'), cardLast4: text(metadata, 'OLD_CARD_LAST4') }),
    oldPaymentMethodId: text(metadata, 'OLD_PAYMENT_METHOD_ID'),
    stripeCustomerId: text(metadata, 'STRIPE_CUSTOMER_ID'),
    stripeCustomerUrl: url && STRIPE_CUSTOMER_URL.test(url) ? url : null,
    newCard: cardText({ cardBrand: text(metadata, 'NEW_CARD_BRAND'), cardLast4: text(metadata, 'NEW_CARD_LAST4') }),
    stopped: !text(metadata, 'NEW_CARD_LAST4'),
    error: text(metadata, 'ERROR'),
    failedAt: failed || null,
  };
}
